/** Strategy writer (docs/13-rules-strategies.md §8): say how you want to trade; Dukun writes the strategy, tests it
 *  on real history and explains the result. The draft and its latest backtest sit beside the conversation, so the
 *  words and the numbers are always in view; saving makes a strategy version that can run as a paper bot. */
import { useEffect, useRef, useState } from "react";
import { Crew } from "../components/Crew.tsx";
import { EquityChart } from "../components/EquityChart.tsx";
import { Shell } from "../components/Shell.tsx";
import { ErrorNote, Loading, Page, Stat } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { invalidate, useApi, useUserEvents } from "../lib/data.ts";
import { dateUtc, pct, usd0 } from "../lib/format.ts";
import { Link, navigate, useLocation } from "../lib/router.tsx";
import type { Check } from "../lib/types.ts";

interface Msg { id: string; role: "user" | "assistant"; body: string; created_at: number; data?: { steps?: { tool: string; ok: boolean }[] } | null }
interface Backtest {
  from: number; to: number; capital: number; checks: Check[];
  summary: { final: number; cagrPct: number; maxDdPct: number; sharpe: number; trades: number; tradesPerWeek: number; feesPaid: number };
  btc: { cagrPct: number; maxDdPct: number; final: number };
  rules: { winRatePct: number; profitFactor: number; avgDealPct: number; avgBars: number; feesPctOfGross: number } | null;
  weights?: { rebalances: number; feesPctOfGross: number; avgExposure: number } | null;
  grid?: { roundTrips: number; gridProfit: number; feesPctOfGridProfit: number; inRangePct: number; coin: { cagrPct: number; maxDdPct: number } } | null;
  daily: { t: number[]; equity: number[]; btc: number[] };
}
interface SessionView { id: string; title: string | null; draft: { name: string; about?: string; timeframe?: string; trade?: { by: string } } | null; words: string[]; backtest: Backtest | null; backtests: number; strategyId: string | null; versionId: string | null }

const IDEAS = [
  "I have $2,000 and want to buy dips on BTC and ETH. Spot only, I can live with a 15% drop.",
  "Swing trade the 10 most traded coins on daily charts, long only, cut losers fast.",
  "Scalp SOL on 5-minute bars with small targets. Futures, at most 3× leverage.",
  "DCA into ETH when it falls, take profit on the bounce. $500 to start.",
  "Run a grid bot on BTC that buys dips and sells bounces inside a range.",
  "Every week, hold the 3 strongest of my coins in equal parts.",
];

/** /strategies/new: start a conversation (optionally from a strategy or with a first message) and go to it. */
export function NewWriter() {
  const { search } = useLocation();
  const started = useRef(false);
  const [err, setErr] = useState<Error | null>(null);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const from = search.get("from"), ask = search.get("ask");
    api<{ id: string }>("/writer/sessions", { method: "POST", body: from ? { fromVersionId: from } : {} })
      .then((r) => navigate(`/writer/${r.id}${ask ? `?ask=${encodeURIComponent(ask)}` : ""}`, true))
      .catch((e) => setErr(e as Error));
  }, [search]);
  return <Shell crumb="Strategies · New"><Page width={1180}><ErrorNote error={err} />{!err && <Loading />}</Page></Shell>;
}

export function Writer({ id }: { id: string }) {
  const { search } = useLocation();
  const { data, error } = useApi<{ session: SessionView; messages: Msg[] }>(`/writer/sessions/${id}`);
  const [session, setSession] = useState<SessionView | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]), [draft, setDraft] = useState(""), [busy, setBusy] = useState(false), [err, setErr] = useState<Error | null>(null);
  const [streaming, setStreaming] = useState(""), [status, setStatus] = useState("");
  const end = useRef<HTMLDivElement>(null), asked = useRef(false);
  useEffect(() => { if (data) { setSession(data.session); setMsgs(data.messages); } }, [data]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs, streaming, status]);
  useUserEvents((m) => {
    if (m.sessionId !== id) return;
    if (m.type === "writer.delta") { setStreaming((s) => s + String(m.text)); setStatus(""); }
    if (m.type === "writer.status") setStatus(String(m.text));
    if (m.type === "writer.draft") setSession(m.session as SessionView);
  });
  const send = async (text: string) => {
    if (!text.trim() || busy) return;
    setBusy(true); setErr(null); setDraft(""); setStreaming(""); setStatus("Reading what you want…");
    setMsgs((x) => [...x, { id: `local-${Date.now()}`, role: "user", body: text.trim(), created_at: Date.now() }]);
    try {
      const r = await api<{ message: Msg; session: SessionView }>(`/writer/sessions/${id}/messages`, { method: "POST", body: { text: text.trim() } });
      setMsgs((x) => [...x, r.message]); setSession(r.session);
    } catch (e) { setErr(e as Error); } finally { setBusy(false); setStreaming(""); setStatus(""); }
  };
  // a first message handed over from the website (?ask=...)
  useEffect(() => {
    const ask = search.get("ask");
    if (!ask || asked.current || !data || data.messages.length) return;
    asked.current = true;
    navigate(`/writer/${id}`, true);
    void send(ask);
  }, [data, search, id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <Shell crumb="Strategies · Writer"><Page width={1180}><ErrorNote error={error} /></Page></Shell>;
  if (!session) return <Shell crumb="Strategies · Writer"><Page width={1180}><Loading /></Page></Shell>;
  return (
    <Shell crumb={<>Strategies · {session.draft?.name ?? "New strategy"}</>}>
      <Page width={1180}>
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
          <section aria-label="Conversation with Dukun" className="flex min-h-[70vh] flex-col gap-4">
            <div className="flex flex-col gap-4" aria-live="polite">
              {msgs.length === 0 && !busy && (
                <div className="flex flex-col gap-4 py-4">
                  <div className="flex items-start gap-3"><Crew character="nebula" size={34} />
                    <div className="flex flex-col gap-2"><h1 className="m-0 text-[22px] font-semibold text-ink-strong">How do you want to trade?</h1>
                      <p className="m-0 text-muted">Say what you want to trade, how much you have, and how big a drop you can live with. I'll write a strategy that fits, test it on years of real Binance prices, and tell you honestly what the test says.</p></div></div>
                  <div className="flex flex-col gap-2 sm:pl-12">{IDEAS.map((q) => <button key={q} className="btn justify-start text-left" onClick={() => void send(q)}>{q}</button>)}</div>
                </div>
              )}
              {msgs.map((m) => m.role === "user"
                ? <div key={m.id} className="ml-auto max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-teal px-4 py-2.5 text-white">{m.body}</div>
                : <div key={m.id} className="flex max-w-[92%] gap-3"><Crew character="nebula" size={30} /><div className="min-w-0 flex-1 whitespace-pre-wrap text-[15px] leading-relaxed">{m.body}</div></div>)}
              {busy && (
                <div className="flex max-w-[92%] gap-3"><Crew character="nebula" size={30} />
                  <div className="min-w-0 flex-1 text-[15px] leading-relaxed">
                    {streaming && <div className="whitespace-pre-wrap">{streaming}</div>}
                    {status && <div className="text-muted">{status}</div>}
                  </div></div>
              )}
              <ErrorNote error={err} />
              <div ref={end} />
            </div>
            <form className="sticky bottom-[72px] mt-auto flex gap-2 bg-bg py-2 md:bottom-0 md:pb-4" onSubmit={(e) => { e.preventDefault(); void send(draft); }}>
              <textarea className="input min-h-[48px] flex-1 resize-none" rows={2} placeholder={session.draft ? "Ask for a change, e.g. “tighter stop” or “try it on 4-hour bars”" : "e.g. I have $1,000, BTC and ETH only, no leverage"}
                value={draft} maxLength={3000} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(draft); } }} aria-label="Message Dukun" />
              <button className="btn-primary self-end" disabled={busy || !draft.trim()}>{busy ? "…" : "Send"}</button>
            </form>
          </section>
          <aside className="flex flex-col gap-4 lg:sticky lg:top-24 lg:self-start" aria-label="The strategy">
            <StrategyPanel s={session} onSaved={setSession} />
          </aside>
        </div>
      </Page>
    </Shell>
  );
}

function StrategyPanel({ s, onSaved }: { s: SessionView; onSaved: (s: SessionView) => void }) {
  const [busy, setBusy] = useState<"save" | "run" | null>(null), [err, setErr] = useState<Error | null>(null), [saved, setSaved] = useState<string | null>(null);
  if (!s.draft) return <section className="card p-5 text-[14px] text-muted">The strategy appears here as soon as it's written, with its backtest beside the words.</section>;
  const save = async (run: boolean) => {
    setBusy(run ? "run" : "save"); setErr(null);
    try {
      const r = await api<{ strategyId: string; versionId: string; version: number }>(`/writer/sessions/${s.id}/save`, { method: "POST" });
      invalidate("/strategies");
      onSaved({ ...s, strategyId: r.strategyId, versionId: r.versionId });
      if (run) navigate(`/bots/new?strategy=${encodeURIComponent(r.versionId)}`);
      else setSaved(`Saved as version ${r.version}.`);
    } catch (e) { setErr(e as Error); } finally { setBusy(null); }
  };
  const b = s.backtest;
  return (
    <>
      <section className="card flex flex-col gap-3 p-5">
        <div className="eyebrow">{s.draft.trade?.by === "grid" ? "Grid" : s.draft.trade?.by === "weights" ? "Weights" : "Signals"} · {s.draft.timeframe ?? "1h"} bars</div>
        <h2 className="m-0 text-[19px] font-semibold text-ink-strong">{s.draft.name}</h2>
        {s.draft.about && <p className="m-0 text-[14px] text-ink/85">{s.draft.about}</p>}
        <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5 text-[14px] leading-relaxed">{s.words.map((w) => <li key={w}>{w}</li>)}</ul>
      </section>
      {b ? <BacktestPanel b={b} tested={s.backtests} /> : <section className="card p-5 text-[14px] text-muted">Not tested yet.</section>}
      <ErrorNote error={err} />
      {saved && <div className="text-[14px] text-muted" aria-live="polite">{saved} <Link to="/strategies">See your strategies</Link></div>}
      <div className="flex flex-wrap gap-2">
        <button className="btn-primary" disabled={!!busy} onClick={() => void save(true)}>{busy === "run" ? "Saving…" : "Run it on paper"}</button>
        <button className="btn" disabled={!!busy} onClick={() => void save(false)}>{busy === "save" ? "Saving…" : s.versionId ? "Save a new version" : "Save"}</button>
      </div>
      <p className="m-0 text-[12px] text-muted">Paper bots trade pretend money on the live market. Past results don't promise future ones; this isn't financial advice.</p>
    </>
  );
}

function BacktestPanel({ b, tested }: { b: Backtest; tested: number }) {
  const s = b.summary, r = b.rules, failed = b.checks.filter((c) => !c.ok);
  return (
    <section className="card flex flex-col gap-4 p-5">
      <div className="eyebrow">Backtest · {dateUtc(b.from)} → {dateUtc(b.to)} · {usd0(b.capital)}</div>
      <div className="grid grid-cols-2 gap-2.5">
        <Stat label="Per year" value={pct(s.cagrPct)} tone={s.cagrPct >= 0 ? "up" : "down"} hint={`Hold BTC ${pct(b.btc.cagrPct)}`} />
        <Stat label="Worst drop" value={pct(s.maxDdPct)} tone="down" hint={`Hold BTC ${pct(b.btc.maxDdPct)}`} />
        <Stat label="Trades" value={s.trades.toLocaleString("en-US")} hint={`${s.tradesPerWeek.toFixed(1)} a week`} />
        {b.weights ? <Stat label="Rebalances" value={b.weights.rebalances.toLocaleString("en-US")} hint={`invested ${Math.round(b.weights.avgExposure * 100)}% of the time`} />
          : b.grid ? <Stat label="Swings" value={b.grid.roundTrips.toLocaleString("en-US")} hint={`in range ${Math.round(b.grid.inRangePct)}% of the time`} />
          : r ? <Stat label="Won" value={`${Math.round(r.winRatePct)}%`} hint={`avg ${pct(r.avgDealPct, 2)} a trade`} /> : <Stat label="Sharpe" value={s.sharpe.toFixed(2)} />}
      </div>
      <EquityChart height={170} t={b.daily.t} series={[{ label: "Strategy", v: b.daily.equity, tone: "accent" }, { label: "Hold BTC", v: b.daily.btc, tone: "muted" }]} />
      {b.grid && <div className="text-[13px] text-muted">Holding the coin: {pct(b.grid.coin.cagrPct)} a year, worst drop {pct(b.grid.coin.maxDdPct)}. Swings earned {usd0(b.grid.gridProfit)}{Number.isFinite(b.grid.feesPctOfGridProfit) ? `, fees took ${Math.round(b.grid.feesPctOfGridProfit)}% of it` : ""}.</div>}
      {r && <div className="text-[13px] text-muted">Fees and slippage {Number.isFinite(r.feesPctOfGross) ? `took ${Math.round(r.feesPctOfGross)}% of the profit before costs` : "came on top of losing trades"} ({usd0(s.feesPaid)}).</div>}
      <div className="flex flex-col">
        {(failed.length ? failed : b.checks).slice(0, 5).map((c) => (
          <div key={c.id} className="flex gap-2.5 border-t border-soft py-2.5 first:border-0">
            <span className={`mt-0.5 shrink-0 text-[13px] font-bold ${c.ok ? "text-ok-ink" : "text-warn-ink"}`} aria-label={c.ok ? "passed" : "warning"}>{c.ok ? "✓" : "!"}</span>
            <div><div className="text-[14px] font-semibold text-ink-strong">{c.title}</div><div className="text-[13px] text-muted">{c.text}</div></div>
          </div>
        ))}
        {failed.length > 0 && failed.length < b.checks.length && <div className="pt-1 text-[13px] text-muted">{b.checks.length - failed.length} other checks passed.</div>}
      </div>
      {tested > 1 && <div className="text-[12px] text-muted">{tested} versions tested in this conversation; the best of several looks better than it is.</div>}
    </section>
  );
}
